import {
    getActiveAdditionalFees,
    getPalletCountFromAdditionalFees,
    getRequiredServiceFeeRow,
    mapAdditionalFeesForApi,
    parseFeeQuantity,
    parseFeeUnitRate,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFees';
import {
    getAdditionalFeeChargeTypeLabel,
    getRequiredAdditionalFeeChargeType,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFeeTypeUtils';

export const CROSS_DOCK_DELIVERY_TYPE = 'CROSS DOCK';

function mapPoMapsForApi(poMaps) {
    return (poMaps ?? []).map((row) => {
        const mapped = {
            poType: row.poType,
            purchaseOrderId: row.purchaseOrderId ?? null,
            packagingPurchaseOrderId: row.packagingPurchaseOrderId ?? null,
        };
        if (row.id != null) {
            mapped.id = row.id;
        }
        return mapped;
    });
}

export const FREIGHT_TYPE_OPTIONS = [
    { value: 'FTL', label: 'FTL' },
    { value: 'LTL', label: 'LTL' },
    { value: 'Rail', label: 'Rail' },
];

export const DEFAULT_CROSS_DOCK_PURCHASE_ORDER_DRAFT = {
    crossDockId: '',
    facilityId: '',
    freightType: '',
    dockDoor: '',
    scheduledDeliveryDate: '',
    scheduledDeliveryTime: '',
    shipDate: '',
    minTemp: '',
    maxTemp: '',
    specialInstructions: '',
    additionalFees: [],
    poMaps: [],
};

function parseOptionalInt(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const normalized = String(value).trim();
    if (!/^-?\d+$/.test(normalized)) {
        return null;
    }
    const parsed = Number.parseInt(normalized, 10);
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeRemovedFeeChargeTypes(removedFeeChargeTypes) {
    if (!removedFeeChargeTypes) {
        return new Set();
    }
    if (removedFeeChargeTypes instanceof Set) {
        return removedFeeChargeTypes;
    }
    return new Set(removedFeeChargeTypes);
}

function validateAdditionalFees(additionalFees, { additionalFeeTypes, removedFeeChargeTypes, requiredChargeType }) {
    const feeFieldErrors = {};
    const feeErrorSet = new Set();
    const removed = normalizeRemovedFeeChargeTypes(removedFeeChargeTypes);
    const rows = getActiveAdditionalFees(additionalFees, removed);
    const requiredFeeLabel = requiredChargeType
        ? getAdditionalFeeChargeTypeLabel(additionalFeeTypes, requiredChargeType)
        : 'Service Fee';

    if (!requiredChargeType || !getRequiredServiceFeeRow(rows, requiredChargeType)) {
        feeFieldErrors.additionalFees = `${requiredFeeLabel} is required`;
        feeErrorSet.add(`${requiredFeeLabel} is required`);
    }

    const seenChargeTypes = new Set();
    rows.forEach((fee) => {
        if (seenChargeTypes.has(fee.chargeType)) {
            feeFieldErrors.additionalFees = 'Each additional fee type can only be added once';
            feeErrorSet.add('Each additional fee type can only be added once');
        }
        seenChargeTypes.add(fee.chargeType);

        const quantity = parseFeeQuantity(fee.quantity);
        if (quantity === null || quantity <= 0) {
            const quantityMessage =
                requiredChargeType && fee.chargeType === requiredChargeType
                    ? `${requiredFeeLabel} quantity (pallet count) must be at least 1`
                    : 'Quantity must be a positive whole number';
            feeFieldErrors[`fee.${fee.chargeType}.quantity`] = quantityMessage;
            feeErrorSet.add(quantityMessage);
        }

        const unitRate = parseFeeUnitRate(fee.unitRate);
        if (unitRate === null || unitRate < 0) {
            feeFieldErrors[`fee.${fee.chargeType}.unitRate`] = 'Rate must be zero or greater';
            feeErrorSet.add('Rate must be zero or greater');
        }

        const totalRate = Number(fee.totalRate);
        if (!Number.isFinite(totalRate) || totalRate < 0) {
            feeErrorSet.add('Each fee must have a valid total amount');
        }
    });

    return { feeFieldErrors, feeErrors: [...feeErrorSet] };
}

function buildCreateRequestBody(
    draft,
    { crossDockId, facilityId, maxTemp, minTemp, removedFeeChargeTypes, requiredChargeType },
) {
    const removed = normalizeRemovedFeeChargeTypes(removedFeeChargeTypes);
    const additionalFees = mapAdditionalFeesForApi(getActiveAdditionalFees(draft.additionalFees, removed));
    const palletCount = getPalletCountFromAdditionalFees(additionalFees, requiredChargeType);

    const body = {
        deliveryType: CROSS_DOCK_DELIVERY_TYPE,
        crossDockId,
        facilityId,
        freightType: draft.freightType,
        additionalFees,
        poMaps: mapPoMapsForApi(draft.poMaps),
    };

    if (palletCount !== null) {
        body.palletCount = palletCount;
    }

    const dockDoor = String(draft.dockDoor ?? '').trim();
    if (dockDoor) {
        body.dockDoor = dockDoor;
    }

    if (draft.scheduledDeliveryDate) {
        body.scheduledDeliveryDate = draft.scheduledDeliveryDate;
    }
    if (draft.scheduledDeliveryTime) {
        body.scheduledDeliveryTime = draft.scheduledDeliveryTime;
    }
    if (draft.shipDate) {
        body.shipDate = draft.shipDate;
    }
    if (minTemp !== null) {
        body.minTemp = minTemp;
    }
    if (maxTemp !== null) {
        body.maxTemp = maxTemp;
    }

    const specialInstructions = String(draft.specialInstructions ?? '').trim();
    if (specialInstructions) {
        body.specialInstructions = specialInstructions;
    }

    return body;
}

/**
 * @param {typeof DEFAULT_CROSS_DOCK_PURCHASE_ORDER_DRAFT} draft
 * @param {{ additionalFeeTypes?: Array<{ chargeType: string, label: string, required: boolean }>, removedFeeChargeTypes?: Set<string>|string[] }} [options]
 */
export function validateCrossDockPurchaseOrderCreate(draft, options = {}) {
    const additionalFeeTypes = options.additionalFeeTypes ?? [];
    const { removedFeeChargeTypes } = options;
    const requiredChargeType = getRequiredAdditionalFeeChargeType(additionalFeeTypes);

    const primaryFieldErrors = {};
    const primaryErrors = [];
    const { feeErrors, feeFieldErrors } = validateAdditionalFees(draft.additionalFees, {
        additionalFeeTypes,
        removedFeeChargeTypes,
        requiredChargeType,
    });

    const crossDockId = Number(draft.crossDockId);
    if (!Number.isFinite(crossDockId) || crossDockId <= 0) {
        primaryFieldErrors.crossDockId = 'Destination cross dock is required';
        primaryErrors.push('Destination cross dock is required');
    }

    const facilityId = Number(draft.facilityId);
    if (!Number.isFinite(facilityId) || facilityId <= 0) {
        primaryFieldErrors.facilityId = 'Facility is required';
        primaryErrors.push('Facility is required');
    }

    if (!draft.freightType) {
        primaryFieldErrors.freightType = 'Freight type is required';
        primaryErrors.push('Freight type is required');
    }

    if (!Array.isArray(draft.poMaps) || draft.poMaps.length === 0) {
        primaryFieldErrors.poMaps = 'At least one associated purchase order is required';
        primaryErrors.push('At least one associated purchase order is required');
    }

    const minTemp = parseOptionalInt(draft.minTemp);
    const maxTemp = parseOptionalInt(draft.maxTemp);
    if (draft.minTemp !== '' && minTemp === null) {
        primaryFieldErrors.minTemp = 'Min temperature must be a whole number';
        primaryErrors.push('Min temperature must be a whole number');
    }
    if (draft.maxTemp !== '' && maxTemp === null) {
        primaryFieldErrors.maxTemp = 'Max temperature must be a whole number';
        primaryErrors.push('Max temperature must be a whole number');
    }
    if (minTemp !== null && maxTemp !== null && minTemp > maxTemp) {
        primaryFieldErrors.maxTemp = 'Max temperature must be greater than or equal to min';
        primaryErrors.push('Max temperature must be greater than or equal to min');
    }

    const isValid = primaryErrors.length === 0 && feeErrors.length === 0;

    return {
        isValid,
        primaryErrors,
        primaryFieldErrors,
        feeErrors,
        feeFieldErrors,
        submitBody: isValid
            ? buildCreateRequestBody(draft, {
                  crossDockId,
                  facilityId,
                  maxTemp,
                  minTemp,
                  removedFeeChargeTypes,
                  requiredChargeType,
              })
            : null,
    };
}

export function validateCrossDockPurchaseOrderUpdate(draft, options = {}) {
    return validateCrossDockPurchaseOrderCreate(draft, options);
}
